import React, { useState } from 'react';
import {
  Container, Paper, Typography, Button, Box, Grid, Card,
  CardActionArea, Divider, Chip,
  FormControl, Select, MenuItem
} from '@mui/material';
import {
  ArrowForward, ArrowBack, Gavel, CheckCircle, Domain,
  VerifiedUser, Security, Star, FlashOn
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';
import ServiceContactForm from '../components/ServiceContactForm';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mcaServicesList = [
  { id: 'all', name: 'All Services', icon: <FlashOn /> },
  { id: 'pvt-annual', name: 'Company Annual Compliance', popular: true, docs: ['Audited Financials', 'DIR-3 KYC Proof', 'Shareholding List'] },
  { id: 'llp-annual', name: 'LLP Annual Compliance', docs: ['Statement of Accounts (Form 8)', 'Annual Return (Form 11)'] },
  { id: 'dir-kyc', name: 'DIR-3 KYC & Reactivation', popular: true, docs: ['Director PAN & Aadhaar', 'Mobile OTP', 'Personal Email OTP'] },
  { id: 'director-change', name: 'Director Addition / Removal', docs: ['DIR-2 Consent Form', 'Board Resolution', 'DIN Details'] },
  { id: 'office-change', name: 'Registered Office Change', docs: ['Utility Bill (Electricity/Gas)', 'NOC from Owner', 'Rent Agreement'] },
  { id: 'dpt3', name: 'DPT-3 Outstanding Loan Filing', docs: ['Loan Agreement Copy', 'Auditor Certificate', 'Bank Statements'] },
  { id: 'adt1', name: 'ADT-1 Auditor Appointment', docs: ['Auditor Written Consent', 'AGM Resolution', 'Auditor Firm Reg No'] },
  { id: 'capital-inc', name: 'Authorised Capital Increase', docs: ['EGM Resolution', 'Altered MOA Copy', 'Stamp Duty Receipt'] }
];

const planFeatures: Record<string, string[]> = {
  basic: ["AOC-4 Financial Statement filing", "MGT-7 Annual Return filing", "Standard email support"],
  standard: ["Includes Basic Package", "DIR-3 Director eKYC (2 Directors)", "ADT-1 Auditor appointment filing", "DPT-3 Loan return filing"],
  premium: ["Includes Standard Package", "Board Resolutions & Minutes drafting", "Statutory Register Maintenance", "Dedicated Company Secretary Retainer"]
};

interface Option { label: string; value: string; subtext?: string; icon: React.ReactNode; }
interface Question { id: number; text: string; description: string; options: Option[]; }

const McaCompliance: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [recommendedService, setRecommendedService] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'standard' | 'premium'>('standard');

  const questions: Question[] = [
    {
      id: 1,
      text: "What is your registered entity structure under MCA?",
      description: "Company Law compliance requirements differ between Private Limited Companies and LLPs.",
      options: [
        { label: "Private Limited Company / OPC", value: "pvt_ltd", subtext: "Requires AOC-4, MGT-7, ADT-1, DPT-3 & DIR-3 KYC", icon: <Domain sx={{ fontSize: 36 }} /> },
        { label: "Limited Liability Partnership (LLP)", value: "llp", subtext: "Requires Form 11, Form 8 & Partner DIR-3 KYC", icon: <Gavel sx={{ fontSize: 36 }} /> },
        { label: "Public Limited / Section 8 NGO", value: "public", subtext: "Full statutory secretarial compliance", icon: <Star sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 2,
      text: "Which ROC statutory filings or corporate modifications are needed?",
      description: "Select your primary corporate compliance priority.",
      options: [
        { label: "Annual Financials & AOC-4 / MGT-7 / Form 11", value: "annual", subtext: "Statutory annual ROC return filing", icon: <Gavel sx={{ fontSize: 36 }} /> },
        { label: "Director eKYC (DIR-3 KYC) / DIN Reactivation", value: "dir_kyc", subtext: "Reactivate deactivated Director Identification Numbers", icon: <VerifiedUser sx={{ fontSize: 36 }} /> },
        { label: "Director Addition / Removal / Address Change", value: "modification", subtext: "DIR-12, INC-22, Authorised capital increase", icon: <Security sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 3,
      text: "Is your company facing daily MCA late filing penalties?",
      description: "MCA imposes a mandatory late penalty fee of ₹100 per day for delayed filing.",
      options: [
        { label: "Yes, filings are overdue (Penalty running)", value: "overdue", subtext: "Fast-track regularization required", icon: <Security sx={{ fontSize: 36 }} /> },
        { label: "No, filing within standard due dates", value: "on_time", subtext: "Standard compliance schedule", icon: <CheckCircle sx={{ fontSize: 36 }} /> }
      ]
    }
  ];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      const service = calculateRecommendedService();
      setRecommendedService(service);
      setShowRegistration(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateRecommendedService = (): string => {
    const q1 = answers[1];
    const q2 = answers[2];

    if (q2 === 'dir_kyc') return 'DIR-3 KYC & Reactivation';
    if (q2 === 'modification') return 'Corporate Modification';
    if (q1 === 'llp') return 'LLP Annual Compliance';
    return 'Pvt Ltd Annual Compliance';
  };

  const activeServiceKey = recommendedService || 'Pvt Ltd Annual Compliance';

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <SEOHead
        title="MCA Compliance Services — Annual Returns, DIR-3 KYC | Fingard Partners"
        description="Stay compliant with MCA regulations. Fingard Partners handles Annual Returns, DIR-3 KYC, ADT-1, DPT-3, and all Ministry of Corporate Affairs filings."
        keywords="MCA Compliance, Annual Return Filing, DIR-3 KYC, ADT-1, DPT-3, Company Compliance India, ROC Filing"
        canonicalUrl="/mca-compliance"
      />
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Chip label="MCA Corporate Compliance Hub" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
        <Typography variant="h4" fontWeight={800} color="#123D6B" gutterBottom>
          Ministry of Corporate Affairs (MCA) Portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
          File annual AOC-4/MGT-7 returns, execute director DIR-3 eKYC, and eliminate daily MCA non-compliance penalties.
        </Typography>
      </Box>

      {/* DIAGNOSTIC WIZARD SECTION */}
      <Container maxWidth="md">
        {!showRegistration ? (
          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                Question {currentStep + 1} of {questions.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(((currentStep + 1) / questions.length) * 100)}% Completed
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 1 }}>
              {questions[currentStep].text}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {questions[currentStep].description}
            </Typography>

            <Grid container spacing={2}>
              {questions[currentStep].options.map((opt, i) => {
                const isSelected = answers[questions[currentStep].id] === opt.value;
                return (
                  <Grid item xs={12} key={i}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#C59B27', transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardActionArea onClick={() => handleOptionSelect(opt.value)} sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Box sx={{ color: isSelected ? '#123D6B' : '#64748b' }}>
                            {opt.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="#0f172a" sx={{ fontSize: '1.05rem' }}>
                              {opt.label}
                            </Typography>
                            {opt.subtext && (
                              <Typography variant="body2" color="text.secondary">
                                {opt.subtext}
                              </Typography>
                            )}
                          </Box>
                          {isSelected && <CheckCircle color="primary" />}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                disabled={currentStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!answers[questions[currentStep].id]}
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, bgcolor: '#123D6B', px: 4, '&:hover': { bgcolor: '#C59B27' } }}
              >
                {currentStep === questions.length - 1 ? 'See Recommendation' : 'Next Step'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip label="AI Diagnostic Match" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
                <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                  Recommended Solution: {activeServiceKey}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on your statutory profile, <strong>{activeServiceKey}</strong> is identified as your required MCA compliance filing.
                </Typography>

                {/* SERVICE OVERRIDE DROPDOWN */}
                <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                    Want a different corporate filing? Choose from dropdown:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={activeServiceKey}
                      onChange={(e) => setRecommendedService(e.target.value)}
                      sx={{ borderRadius: 2.5, bgcolor: '#F8FAFC', fontWeight: 600, color: '#123D6B' }}
                    >
                      <MenuItem value="Pvt Ltd Annual Compliance">Pvt Ltd Annual Compliance (AOC-4 & MGT-7)</MenuItem>
                      <MenuItem value="LLP Annual Compliance">LLP Annual Compliance (Form 8 & 11)</MenuItem>
                      <MenuItem value="DIR-3 KYC & Reactivation">Director DIR-3 KYC & DIN Reactivation</MenuItem>
                      <MenuItem value="Corporate Modification">Director Addition/Removal & Address Change</MenuItem>
                      <MenuItem value="DPT-3 Outstanding Loan Filing">DPT-3 Return of Deposits & Loans</MenuItem>
                      <MenuItem value="ADT-1 Auditor Appointment">ADT-1 Statutory Auditor Appointment</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Package Tiers Selection (Scope only, no public prices) */}
              <Typography variant="subtitle1" fontWeight="bold" color="#0f172a" sx={{ mb: 1.5 }}>
                Select Secretarial Scope:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {(['basic', 'standard', 'premium'] as const).map((plan) => {
                  const isSel = selectedPlan === plan;
                  return (
                    <Grid item xs={12} sm={4} key={plan}>
                      <Card
                        variant="outlined"
                        onClick={() => setSelectedPlan(plan)}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          borderRadius: 3,
                          borderColor: isSel ? '#123D6B' : '#E2E8F0',
                          borderWidth: isSel ? 2 : 1,
                          bgcolor: isSel ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        {plan === 'standard' && (
                          <Chip label="Most Popular" color="secondary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', mt: 1 }}>
                          {plan} Package
                        </Typography>
                        <Box sx={{ my: 1.5 }}>
                          <Chip label="Custom CA Quote" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }} />
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        {planFeatures[plan].map((feat, idx) => (
                          <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, textAlign: 'left' }}>
                            ✓ {feat}
                          </Typography>
                        ))}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Service Contact & Quotation Request Form */}
            <ServiceContactForm
              serviceCategory="mca"
              selectedService={activeServiceKey + ' (' + selectedPlan.toUpperCase() + ' Scope)'}
              selectedPackageTier={selectedPlan}
              diagnosticAnswers={answers}
              onBackToQuiz={() => setShowRegistration(false)}
            />
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default McaCompliance;
